/**
 * The equipment of the open world: one set per biome, six pieces, and every
 * piece in the four rarities (`docs/adr/0012-loot-tiers.md`).
 *
 * A piece has one drawing, its own (ADR 0005 and 0006): the four rarities of
 * the hunter's hood are the same hood, told apart by the colour of their slot
 * in the bag and by their stats. They share the level of their biome's entry,
 * so rarity is luck, not a later step: a legendary wooden sword is still a
 * wooden sword, only a much better one.
 *
 * The common one keeps the bare id (`wooden-sword`), the others take the
 * rarity as a suffix (`wooden-sword-epic`). Validated by `ItemTemplateSchema`
 * with the rest, in `items.ts`.
 */
import { roundDps } from '../rules/dps.js';
export const BIOME_SETS = [
    {
        biome: 'meadow',
        level: 1,
        pieces: {
            weapon: { id: 'shepherd-crook', name: 'Houlette ferrée' },
            offhand: { id: 'shepherd-shield', name: 'Bouclier d’osier' },
            helmet: { id: 'shepherd-hat', name: 'Chapeau de paille' },
            chest: { id: 'shepherd-chest', name: 'Pelisse de berger' },
            gloves: { id: 'shepherd-gloves', name: 'Gants de berger' },
            boots: { id: 'shepherd-boots', name: 'Guêtres de berger' },
        },
    },
    {
        biome: 'forest',
        level: 5,
        pieces: {
            weapon: { id: 'hunter-sword', name: 'Coutelas du chasseur' },
            offhand: { id: 'hunter-shield', name: 'Bouclier du chasseur' },
            helmet: { id: 'hunter-helm', name: 'Capuche du chasseur' },
            chest: { id: 'hunter-chest', name: 'Brigandine du chasseur' },
            gloves: { id: 'hunter-gloves', name: 'Gants du chasseur' },
            boots: { id: 'hunter-boots', name: 'Bottes du chasseur' },
        },
    },
    {
        biome: 'swamp',
        level: 8,
        pieces: {
            weapon: { id: 'scale-sword', name: 'Lame d’écailles' },
            offhand: { id: 'scale-shield', name: 'Bouclier d’écailles' },
            helmet: { id: 'scale-helm', name: 'Casque d’écailles' },
            chest: { id: 'scale-chest', name: 'Cuirasse d’écailles' },
            gloves: { id: 'scale-gloves', name: 'Gantelets d’écailles' },
            boots: { id: 'scale-boots', name: 'Bottes d’écailles' },
        },
    },
    {
        biome: 'desert',
        level: 12,
        pieces: {
            weapon: { id: 'pharaoh-sword', name: 'Khépesh du pharaon' },
            offhand: { id: 'pharaoh-shield', name: 'Bouclier du pharaon' },
            helmet: { id: 'pharaoh-helm', name: 'Némès du pharaon' },
            chest: { id: 'pharaoh-chest', name: 'Pectoral du pharaon' },
            gloves: { id: 'pharaoh-gloves', name: 'Brassards du pharaon' },
            boots: { id: 'pharaoh-boots', name: 'Sandales du pharaon' },
        },
    },
    {
        biome: 'snow',
        level: 16,
        pieces: {
            weapon: { id: 'frost-sword', name: 'Épée de givre' },
            offhand: { id: 'frost-shield', name: 'Bouclier de givre' },
            helmet: { id: 'frost-helm', name: 'Casque de givre' },
            chest: { id: 'frost-chest', name: 'Plastron de givre' },
            gloves: { id: 'frost-gloves', name: 'Gantelets de givre' },
            boots: { id: 'frost-boots', name: 'Bottes de givre' },
        },
    },
];
/**
 * The tutorial's wooden set (`data/tutorial.ts`): the tutor sells it, the
 * smelter's and the smith's lessons use it, and nothing drops it, so the
 * meadow keeps gear of its own. It stays the meadow's for the smith's
 * materials, and keeps its four rarities for the fusion lesson.
 */
export const TUTORIAL_GEAR_SET = {
    biome: 'meadow',
    level: 1,
    lootable: false,
    pieces: {
        weapon: { id: 'wooden-sword', name: 'Épée en bois' },
        offhand: { id: 'wooden-shield', name: 'Rondache en bois' },
        helmet: { id: 'bark-helm', name: 'Casque d’écorce' },
        chest: { id: 'bark-chest', name: 'Plastron d’écorce' },
        gloves: { id: 'wooden-gloves', name: 'Gantelets en bois' },
        boots: { id: 'wooden-boots', name: 'Sabots en bois' },
    },
};
const RARITIES = ['common', 'rare', 'epic', 'legendary'];
/**
 * A piece's worth grows with its level, and rarity multiplies it: a
 * legendary is about twice its common. Across biomes the next set's common
 * still outdoes this one's rare, so moving on pays more than farming.
 */
const RARITY_POWER = {
    common: 1,
    rare: 1.3,
    epic: 1.65,
    legendary: 2.1,
};
const RARITY_PRICE = { common: 1, rare: 3, epic: 8, legendary: 25 };
/** A sword and a shield are the Warrior's; the rest fits any class. */
const WARRIOR_ONLY = new Set(['weapon', 'offhand']);
/**
 * Share of a piece's power it gives as damage per second (ADR 0015). The
 * weapon carries the whole of it; the rest add a little each, so the outfit's
 * total is nearly three weapons and a full set still counts.
 */
const DPS_SHARE = {
    weapon: 1,
    chest: 0.4,
    gloves: 0.4,
    helmet: 0.3,
    boots: 0.3,
    offhand: 0.3,
};
/**
 * Defence per point of power, for the armour alone. At least one, so a rarer
 * piece always gives more; a full snow legendary set at +10 comes to about
 * 170, enough to shrug off the meadow (`rules/combat.ts`, `mitigation`).
 */
const DEFENCE_SHARE = {
    chest: 2,
    helmet: 1.5,
    offhand: 1.5,
    gloves: 1,
    boots: 1,
};
/**
 * Health per point of power, for the heaviest armour: what the Warrior tanks
 * with. Large on purpose (ADR 0015, 2026-09-30): the naked body at level 20 is
 * about a fifth of the best outfit's health, so a raid without armour is lost.
 */
const HEALTH_SHARE = { chest: 8, helmet: 5, offhand: 5 };
function bonusOf(piece, power) {
    // Power rises by a point at least per rarity and the smallest share is a
    // fifth, so a rarer piece still gives 0.2 dps more once rounded to tenths.
    const dps = roundDps(power * DPS_SHARE[piece]);
    const defence = DEFENCE_SHARE[piece];
    const health = HEALTH_SHARE[piece];
    return {
        dps,
        ...(defence === undefined ? {} : { defence: Math.round(defence * power) }),
        ...(health === undefined ? {} : { health: health * power }),
    };
}
/**
 * A piece's power from its level and rarity: what its stats, its price and
 * its affixes (ADR 0057) are measured in. At least one point per step of
 * rarity: at level 1 the multipliers alone would round two rarities to the
 * same piece.
 */
export function piecePower(level, rarity, rank = 0) {
    return Math.max(1 + rank, Math.round((1 + 0.35 * level) * RARITY_POWER[rarity]));
}
export function setPieceId(baseId, rarity) {
    return rarity === 'common' ? baseId : `${baseId}-${rarity}`;
}
/**
 * One piece of gear by the sets' rules: its power from its level and its
 * rarity, its stats and its price from its power. `rank` keeps two rarities
 * of a low set apart; a piece on its own (a quest's, `quest-gear.ts`) leaves it.
 * `powerLevel` measures it above what it requires: Shefira's set, worn at the
 * cap and worth more (`raid-gear.ts`), which `source` names.
 */
export function gearTemplate(piece) {
    const { id, name, slot, rarity, level, rank = 0 } = piece;
    const art = piece.art ?? id;
    const power = piecePower(piece.powerLevel ?? level, rarity, rank);
    return {
        id,
        name,
        kind: 'equipment',
        slot,
        rarity,
        levelRequired: level,
        ...(piece.lootable === false ? { lootable: false } : {}),
        ...(piece.source === undefined ? {} : { source: piece.source }),
        ...(WARRIOR_ONLY.has(slot) ? { classes: ['warrior'] } : {}),
        bonus: bonusOf(slot, power),
        // Every piece is drawn, gauntlets and boots included (ADR 0014).
        sprite: `overlay.${art}`,
        icon: `icon.${art}`,
        sellPrice: Math.max(1, Math.round((power * level * RARITY_PRICE[rarity]) / 2)),
    };
}
function templatesOf(set) {
    return Object.entries(set.pieces).flatMap(([slot, { id, name }]) => RARITIES.map((rarity, rank) => gearTemplate({
        id: setPieceId(id, rarity),
        name,
        slot,
        rarity,
        level: set.level,
        art: id,
        rank,
        ...(set.lootable === false ? { lootable: false } : {}),
    })));
}
export const BIOME_SET_TEMPLATES = [...BIOME_SETS, TUTORIAL_GEAR_SET].flatMap(templatesOf);
//# sourceMappingURL=biome-sets.js.map